import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost, axiosPut } from '../api/axios';
import { toast } from 'react-toastify';
import { BackgroundJob } from './useBackgroundJobs';

export type JobExecutorPreference = 'auto' | 'backend' | 'lambda';
export type JobKind = 'faf' | 'scheduled';

export interface JobLastRun {
  id: number;
  status: string;
  queued_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  last_error: string | null;
}

export interface SchedulerLiveness {
  last_tick_at: string | null;
  age_seconds: number | null;
  stale_after_seconds: number;
  stale: boolean;
}

export interface JobControl {
  handler_key: string;
  job_kind: JobKind;
  enabled: boolean;
  executor: JobExecutorPreference;
  schedule_expr: string | null;
  timezone: string | null;
  updated_at: string | null;
  source: 'curated' | 'discovered' | 'both' | 'scheduled';
  label: string | null;
  description: string | null;
  example_args: unknown[] | null;
  example_kwargs: Record<string, unknown> | null;
  effective_executor: 'backend' | 'lambda';
  hard_denylisted: boolean;
  executor_locked: boolean;
  last_run: JobLastRun | null;
  next_run: string | null;
}

export interface JobControlsResponse {
  count: number;
  data: JobControl[];
  scheduler?: SchedulerLiveness;
}

export interface JobControlUpdate {
  enabled?: boolean;
  executor?: JobExecutorPreference;
  schedule_expr?: string | null;
  timezone?: string | null;
}

export interface RunBackgroundJobPayload {
  handler_key?: string;
  module?: string;
  function?: string;
  args?: unknown[];
  kwargs?: Record<string, unknown>;
  priority?: number;
  force?: boolean;
}

export type RunJobResult =
  | BackgroundJob
  | {
      handler_key: string;
      job_kind: 'scheduled';
      status: string;
      dispatch?: string;
      id?: number;
    };

const getJobControls = async (): Promise<JobControlsResponse> => {
  const response = await axiosGet('/job_controls', {}, 'v3');
  return response.data;
};

export const useJobControls = () => {
  return useQuery<JobControlsResponse>(['job_controls'], () => getJobControls());
};

const updateJobControlRequest = async ({
  handlerKey,
  update,
}: {
  handlerKey: string;
  update: JobControlUpdate;
}): Promise<JobControl> => {
  const response = await axiosPut(`/job_controls/${encodeURIComponent(handlerKey)}`, update, 'v3');
  return response.data;
};

export const useUpdateJobControl = () => {
  const queryClient = useQueryClient();
  return useMutation(updateJobControlRequest, {
    onSuccess: (updated) => {
      queryClient.setQueryData<JobControlsResponse>(['job_controls'], (old) => {
        if (!old) return old as any;
        return {
          ...old,
          data: old.data.map((row) => (row.handler_key === updated.handler_key ? updated : row)),
        };
      });
      toast.success(`Updated ${updated.label || updated.handler_key}`);
    },
    onError: (err: any) => {
      const message = err?.response?.data?.error || 'Failed to update job control';
      toast.error(message);
    },
  });
};

const runBackgroundJobRequest = async (payload: RunBackgroundJobPayload): Promise<RunJobResult> => {
  const response = await axiosPost('/background_jobs/run', payload, 'v3');
  return response.data;
};

export const useRunBackgroundJob = () => {
  const queryClient = useQueryClient();
  return useMutation(runBackgroundJobRequest, {
    onSuccess: (result) => {
      if (result && 'id' in result) {
        queryClient.invalidateQueries(['background_jobs']);
        queryClient.invalidateQueries(['background_job_stats']);
        toast.success(`Queued job #${result.id}`);
        return;
      }
      queryClient.invalidateQueries(['job_controls']);
      toast.success(`Triggered ${(result as any).handler_key}`);
    },
    onError: (err: any) => {
      const message = err?.response?.data?.error || 'Failed to run job';
      toast.error(message);
    },
  });
};
